import { useEffect, ReactNode, useState, useRef } from 'react'
import { theme } from '../../theme/colors'

interface ModalProps {
  readonly show: boolean
  readonly handleClose: () => void
  readonly title?: string | ReactNode
  readonly children?: ReactNode
  readonly centered?: boolean
  readonly className?: string
  readonly dataTestId?: string
  readonly id?: string
  readonly isConfirmationDialog?: boolean
  readonly isLevelTwoModal?: boolean
  readonly loading?: boolean
  readonly onCloseCheck?: () => boolean
  readonly size?: 'sm' | 'md' | 'lg' | 'full'
  readonly style?: React.CSSProperties
  readonly draggable?: boolean
}

interface ModalBodyProps {
  readonly children: ReactNode
  readonly style?: React.CSSProperties
}

interface ModalFooterProps {
  readonly children: ReactNode
}

interface ModalFooterButtonProps {
  readonly children: ReactNode
  readonly onClick: () => void
  readonly variant?: 'primary' | 'secondary' | 'link'
  readonly disabled?: boolean
}

function ModalBody({ children, style, className = '', ...rest }: ModalBodyProps & { className?: string }) {
  return (
    <div className={`modal-body ${className}`} style={{ ...style }} {...rest}>
      {children}
    </div>
  )
}

function ModalFooterButton({ children, onClick, variant = 'primary', disabled }: ModalFooterButtonProps) {
  const buttonStyle = {
    padding: '8px 20px',
    border: 'none',
    borderRadius: '3px',
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: '14px',
    fontWeight: 400,
    backgroundColor: variant === 'primary' ? '#3374C8' : 'transparent',
    color: variant === 'primary' ? 'white' : '#3374C8',
    opacity: disabled ? 0.65 : 1,
    textDecoration: variant === 'link' ? 'none' : undefined
  }

  return (
    <button onClick={disabled ? undefined : onClick} style={buttonStyle} disabled={disabled}>
      {children}
    </button>
  )
}

function ModalFooter({ children }: ModalFooterProps) {
  return (
    <div
      className="modal-footer"
      style={{
        padding: '15px',
        fontSize: '12px',
        display: 'flex',
        justifyContent: 'flex-end',
        backgroundColor: theme.colors.selectedBackground
      }}
    >
      {children}
    </div>
  )
}

ModalFooter.Button = ModalFooterButton

export function Modal({
  show,
  handleClose,
  title = '',
  children,
  className = '',
  dataTestId,
  isConfirmationDialog = false,
  loading = false,
  onCloseCheck,
  size = 'md',
  style,
  draggable = true,
  id = '',
  isLevelTwoModal = false
}: Readonly<Omit<ModalProps, 'centered'>>) {
  const [position, setPosition] = useState({ x: 0, y: 30 })
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
  const modalRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && show) {
        if (onCloseCheck && !onCloseCheck()) {
          return
        }
        handleClose()
      }
    }

    if (show) {
      document.addEventListener('keydown', handleEscape)
      document.body.style.overflow = 'hidden'
      // Add modal-open class for toast z-index to work properly
      document.body.classList.add('modal-open')
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = 'unset'
      document.body.classList.remove('modal-open')
    }
  }, [show, handleClose, onCloseCheck])

  useEffect(() => {
    if (!draggable) return

    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging && modalRef.current) {
        let deltaX = e.clientX - dragStart.x
        let deltaY = e.clientY - dragStart.y

        // Get current modal dimensions and position
        const modalRect = modalRef.current.getBoundingClientRect()
        const viewportWidth = window.innerWidth
        const viewportHeight = window.innerHeight

        // Calculate where modal top would be with this delta
        // Current top is modalRect.top, we want to move by deltaY from initial position
        // We need to figure out what the new top would be
        const currentCenter = viewportHeight / 2 + 30 // paddingTop from overlayStyle
        const modalHalfHeight = modalRect.height / 2
        const newTop = currentCenter - modalHalfHeight + deltaY

        // Minimum top position (60px to stay below browser chrome)
        const minTop = 60

        // If new top would be less than minTop, adjust deltaY
        if (newTop < minTop) {
          deltaY = minTop - (currentCenter - modalHalfHeight)
        }

        // Also prevent going too far down
        const maxTop = viewportHeight - 100 // Keep at least 100px visible
        if (newTop > maxTop) {
          deltaY = maxTop - (currentCenter - modalHalfHeight)
        }

        // Prevent going too far left or right (keep at least 100px visible)
        const currentCenterX = viewportWidth / 2
        const modalHalfWidth = modalRect.width / 2
        const newLeft = currentCenterX - modalHalfWidth + deltaX
        const newRight = currentCenterX + modalHalfWidth + deltaX

        if (newLeft < -modalHalfWidth + 100) {
          deltaX = -modalHalfWidth + 100 - (currentCenterX - modalHalfWidth)
        }
        if (newRight > viewportWidth + modalHalfWidth - 100) {
          deltaX = viewportWidth + modalHalfWidth - 100 - (currentCenterX + modalHalfWidth)
        }

        setPosition({
          x: deltaX,
          y: deltaY
        })
      }
    }

    const handleMouseUp = () => {
      setIsDragging(false)
    }

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isDragging, dragStart, draggable])

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!draggable) return
    // Store the initial click position
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    })
    setIsDragging(true)
  }

  if (!show) return null

  const getWidth = () => {
    // Use 100vw for small screens, otherwise use default widths
    if (typeof window !== 'undefined' && window.innerWidth <= 600) {
      return '100vw'
    }
    switch (size) {
      case 'sm':
        return '400px'
      case 'lg':
        return '900px'
      case 'full':
        return '90vw'
      default:
        return '600px'
    }
  }

  const overlayStyle = {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    zIndex: isLevelTwoModal ? 1070 : isConfirmationDialog ? 1060 : 1050,
    paddingTop: 30
  }

  const modalStyle = {
    backgroundColor: 'white',
    borderRadius: '0px',
    width: getWidth(),
    maxWidth: '90vw',
    maxHeight: '85vh',
    overflow: 'visible',
    display: 'flex',
    flexDirection: 'column' as const,
    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
    transform:
      draggable && (position.x !== 0 || position.y !== 0)
        ? `translate(${position.x}px, ${position.y}px)`
        : 'translateY(0)',
    animation: 'slideInFromTop 0.25s ease-out',
    cursor: isDragging ? 'grabbing' : 'default',
    ...style
  }

  const headerStyle = {
    padding: '0px 0px 0px 18px',
    borderBottom: `1px solid ${theme.colors.border}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    minHeight: 43,
    cursor: draggable ? 'grab' : 'default',
    userSelect: 'none' as const
  }

  const closeButtonStyle = {
    background: 'none',
    border: 'none',
    paddingLeft: 15,
    paddingRight: 15,
    fontSize: '2rem',
    cursor: 'pointer',
    color: '#6b7280'
  }

  return (
    <>
      <style>
        {`
          @keyframes slideInFromTop {
            from {
              transform: translateY(-100%);
              opacity: 0;
            }
            to {
              transform: translateY(0);
              opacity: 1;
            }
          }
          @keyframes modalSpinnerRotate {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          @media (max-width: 600px) {
            .modal-footer {
              flex-direction: column;
              align-items: stretch;
            }
            .modal-body {
              padding: 10px;
            }
            /* Responsive modal container */
            .modal-responsive {
              width: 100vw !important;
              max-width: 100vw !important;
              max-height: 100vh !important;
              border-radius: 0 !important;
            }
          }
        `}
      </style>
      <div style={overlayStyle} data-testid={dataTestId} id={id} className="theme-sandman">
        <div
          ref={modalRef}
          style={modalStyle}
          className={
            (className ? className + ' ' : '') +
            (typeof window !== 'undefined' && window.innerWidth <= 600 ? 'modal-responsive' : '')
          }
        >
          {title && (
            <div style={headerStyle} onMouseDown={handleMouseDown}>
              <h4 style={{ margin: 0, fontSize: 18, fontWeight: '600', color: theme.colors.tableHeaderColor }}>
                {title}
              </h4>
              <button
                onClick={() => {
                  if (onCloseCheck && !onCloseCheck()) {
                    return
                  }
                  handleClose()
                }}
                style={closeButtonStyle}
                aria-label="Close modal"
              >
                ×
              </button>
            </div>
          )}
          <div style={{ ...(style ? style : { flex: 1, overflow: 'auto' }), position: 'relative' }}>
            {children}
            {loading && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 1000,
                  cursor: 'wait'
                }}
                data-testid="modal-loading-overlay"
              >
                <div
                  style={{
                    width: 48,
                    height: 48,
                    border: '4px solid #e5e7eb',
                    borderTop: '4px solid #3274C8',
                    borderRadius: '50%',
                    animation: 'modalSpinnerRotate 0.8s linear infinite'
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

Modal.Body = ModalBody
Modal.Footer = ModalFooter as typeof ModalFooter & { Button: typeof ModalFooterButton }

export default Modal
